// National Rail stations: where they are (public/nr-stations.json, from
// NaPTAN), which are the same place as a TfL station (one dot and one card
// for both), and their departure boards (/nr/station?crs=).

import type { Station } from '@tol/core/types';

export interface NrStation {
  readonly crs: string;
  readonly name: string;
  readonly lat: number;
  readonly lon: number;
}

const isRecord = (v: unknown): v is Readonly<Record<string, unknown>> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

// public/nr-stations.json's stations, skipping any malformed one.
export function parseStations(data: unknown): NrStation[] {
  const list = isRecord(data) ? data['stations'] : undefined;
  if (!Array.isArray(list)) return [];
  const raw: readonly unknown[] = list;
  const out: NrStation[] = [];
  for (const s of raw) {
    if (!isRecord(s)) continue;
    const crs = str(s['crs']);
    const name = str(s['name']);
    const lat = num(s['lat']);
    const lon = num(s['lon']);
    if (crs !== undefined && name !== undefined && lat !== undefined && lon !== undefined) {
      out.push({ crs, name, lat, lon });
    }
  }
  return out;
}

// /nr/stations: the CRS codes with trains today, or null if unreadable.
export function parseServed(data: unknown): ReadonlySet<string> | null {
  const list = isRecord(data) ? data['stations'] : undefined;
  if (!Array.isArray(list)) return null;
  const raw: readonly unknown[] = list;
  return new Set(raw.filter((c): c is string => typeof c === 'string'));
}

// A name reduced for comparing across TfL and National Rail: "London
// Waterloo" and "Waterloo", "Elephant & Castle" and "Elephant and Castle",
// "Abbey Wood (London)" and "Abbey Wood".
function stationKey(name: string): string {
  return name
    .toLowerCase()
    .replaceAll(/\([^)]*\)/gu, '')
    .replaceAll('&', ' and ')
    .replaceAll(/\b(rail|underground|dlr)? ?station\b/gu, '')
    .replace(/^london /u, '')
    .replaceAll(/[^a-z0-9]+/gu, '');
}

// m: the same name within this distance is the same station; any name
// within the smaller one is (TfL and NaPTAN put a station's point in
// different places, and name some differently: Kings Cross St Pancras).
const SAME_NAME_M = 500;
const SAME_PLACE_M = 80;

// CRS -> the TfL stations it's the same place as (Richmond is two: the
// District line's and the Overground's). dist: meters between points.
export function matchStations(
  nr: readonly NrStation[],
  tfl: readonly Pick<Station, 'id' | 'name' | 'lat' | 'lon'>[],
  dist: (
    a: { readonly lat: number; readonly lon: number },
    b: { readonly lat: number; readonly lon: number },
  ) => number,
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const s of nr) {
    const key = stationKey(s.name);
    const same: string[] = [];
    for (const t of tfl) {
      const d = dist(s, t);
      if (d <= SAME_PLACE_M || (d <= SAME_NAME_M && stationKey(t.name) === key)) same.push(t.id);
    }
    if (same.length > 0) out.set(s.crs, same);
  }
  return out;
}

// The stations drawn as their own dots: served, and not a TfL station.
export function stationFeatures(
  stations: readonly NrStation[],
  shown: (s: NrStation) => boolean,
): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: 'FeatureCollection',
    features: stations
      .filter((s) => shown(s))
      .map((s) => ({
        type: 'Feature',
        properties: { crs: s.crs, name: s.name },
        geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
      })),
  };
}

// One train on a station's board.
export interface BoardRow {
  readonly uid: string;
  readonly headcode: string;
  readonly lineId: string | null;
  readonly operator: string;
  readonly origin: string;
  readonly destination: string;
  // ms: the timetabled time here (departure, or arrival where it ends here),
  // and the forecast (Darwin's, else TRUST's delay), or null when there's
  // none.
  readonly time: number;
  readonly expected: number | null;
  // No forecast time, but late ("Delayed").
  readonly delayed: boolean;
  readonly platform: string | null;
  readonly terminates: boolean;
  readonly cancelled: boolean;
  // Why it's late or cancelled, short: "Delayed by a speed restriction".
  readonly reason: string | null;
}

// Darwin's "This train has been delayed by ..." without the preamble.
const shortReason = (text: string | undefined): string | null => {
  const rest = text?.replace(/^This (train|service) has been /u, '').trim() ?? '';
  return rest === '' ? null : rest.charAt(0).toUpperCase() + rest.slice(1);
};

// /nr/station's rows, or null if it isn't that station's board.
export function parseBoard(data: unknown, crs: string): BoardRow[] | null {
  if (!isRecord(data) || data['crs'] !== crs || !Array.isArray(data['rows'])) return null;
  const raw: readonly unknown[] = data['rows'];
  const rows: BoardRow[] = [];
  for (const r of raw) {
    if (!isRecord(r)) continue;
    const uid = str(r['uid']);
    const time = num(r['time']);
    if (uid === undefined || time === undefined) continue;
    const op = isRecord(r['operator']) ? r['operator'] : {};
    rows.push({
      uid,
      headcode: str(r['headcode']) ?? '',
      lineId: str(op['lineId']) ?? null,
      operator: str(op['name']) ?? 'National Rail',
      origin: str(r['origin']) ?? '',
      destination: str(r['destination']) ?? '',
      time,
      expected: num(r['expected']) ?? null,
      platform: str(r['platform']) ?? null,
      terminates: r['terminates'] === true,
      cancelled: r['cancelled'] === true,
      delayed: r['delayed'] === true,
      reason: shortReason(str(r['reason'])),
    });
  }
  return rows;
}
